import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  MenuSeparator
} from '@headlessui/react'
import {
  AdjustmentsHorizontalIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowUpRightIcon,
  BookOpenIcon,
  ChevronUpDownIcon,
  MegaphoneIcon,
  UserCircleIcon
} from '@heroicons/react/20/solid'
import { Auth } from 'aws-amplify'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import clsx from 'clsx'
import ProfilePicture from '../assets/images/profilepicture.png'
import trackEvents from '../constants/track-events'
import { usePartnerContext, useUserContext } from '../context'
import { usePricingAdminOverride } from '../features/pricing/admin-override'
import { trackEvent } from '../helpers/analytics'
import AdminPricingDialog from './pricing/admin-pricing-dialog'
import Badge from './tailwind/Badge'

const TRANSLATION_LOCATION = 'components.layout.'

const ITEM_CLASS_NAME =
  'group flex w-full items-center gap-x-2 rounded-md px-2 py-1.5 text-left text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900'

const ICON_CLASS_NAME =
  'size-4 shrink-0 text-gray-400 group-data-[focus]:text-gray-500'

const LEGAL_LINKS = [
  { key: 'imprint', href: 'https://www.aivy.app/impressum' },
  { key: 'data_protection', href: 'https://www.aivy.app/datenschutz' }
]

const Separator = () => (
  <MenuSeparator className='-mx-1 my-1 h-px bg-gray-100' />
)

const ProfileDropdown = ({ isSidebarCollapsed }) => {
  const { t } = useTranslation()
  const { cognitoUser, activeSpace, isAdmin } = useUserContext()
  const { partner } = usePartnerContext()

  // Pricing-Simulation nur für Aivy-Admins. Der Dialog liegt außerhalb von
  // <Menu>, weil das Menü beim Klick schließt und Eingaben darin kaputt sind.
  const pricingOverride = usePricingAdminOverride({
    isAdmin,
    partnerId: partner?.id
  })
  const [isPricingDialogOpen, setIsPricingDialogOpen] = useState(false)

  function signOut() {
    trackEvent(trackEvents.LOGOUT)
    Auth.signOut()
  }

  const { firstname, lastname, invitation_firstname, invitation_lastname } =
    activeSpace.person

  const name = `${firstname || invitation_firstname || ''} ${
    lastname || invitation_lastname || ''
  }`.trim()
  const { email } = cognitoUser.attributes

  // Ohne Namen steht die E-Mail in der ersten Zeile, statt klein und grau
  // allein unter einer leeren Zeile
  const primaryLine = name || email

  return (
    <>
      <Menu as='div' className='relative'>
        <MenuButton className='group flex w-full items-center gap-x-2.5 overflow-hidden rounded-md p-1 text-left transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brightBlue data-[open]:bg-white/10'>
          <img
            className='size-8 shrink-0 rounded-full bg-gray-300'
            src={ProfilePicture}
            alt=''
          />
          <span
            className={clsx(
              'flex min-w-0 flex-1 flex-col transition-opacity duration-200 motion-reduce:transition-none',
              isSidebarCollapsed && 'opacity-0'
            )}
          >
            <span className='truncate text-sm/4 font-medium text-white'>
              {primaryLine}
            </span>
            {name && (
              <span className='truncate text-xs/4 text-gray-400'>{email}</span>
            )}
          </span>
          <ChevronUpDownIcon
            className={clsx(
              'size-4 shrink-0 text-gray-400 transition-opacity group-hover:text-gray-300',
              isSidebarCollapsed && 'opacity-0'
            )}
            aria-hidden='true'
          />
        </MenuButton>
        <MenuItems
          transition
          anchor={
            isSidebarCollapsed
              ? { to: 'right end', gap: 20 }
              : { to: 'top start', gap: 4 }
          }
          className={clsx(
            'z-50 rounded-lg bg-white p-1 shadow-lg ring-1 ring-gray-900/5 focus:outline-none',
            'transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0',
            isSidebarCollapsed
              ? 'w-60 origin-bottom-left'
              : 'w-[var(--button-width)] origin-bottom'
          )}
        >
          {/* Eingeklappt sieht man Name und E-Mail sonst nirgends */}
          <div className='flex items-center gap-x-2.5 px-2 py-1.5'>
            <img
              className='size-8 shrink-0 rounded-full bg-gray-300'
              src={ProfilePicture}
              alt=''
            />
            <div className='flex min-w-0 flex-col'>
              <span className='truncate text-sm/4 font-medium text-gray-900'>
                {primaryLine}
              </span>
              {name && (
                <span className='truncate text-xs/4 text-gray-500'>
                  {email}
                </span>
              )}
            </div>
          </div>
          <Separator />
          <MenuItem>
            <Link
              to='/settings/personal'
              onClick={() => trackEvent(trackEvents.NAVIGATE_TO_PROFILE)}
              className={ITEM_CLASS_NAME}
            >
              <UserCircleIcon className={ICON_CLASS_NAME} aria-hidden='true' />
              {t(TRANSLATION_LOCATION + 'navigation_view_profile')}
            </Link>
          </MenuItem>
          <Separator />
          <MenuItem>
            <Link
              to='/test-library'
              onClick={() => trackEvent(trackEvents.NAVIGATE_TO_TEST_LIBRARY)}
              className={ITEM_CLASS_NAME}
            >
              <BookOpenIcon className={ICON_CLASS_NAME} aria-hidden='true' />
              <span className='flex-1'>
                {t(TRANSLATION_LOCATION + 'navigation_test_library')}
              </span>
            </Link>
          </MenuItem>
          <MenuItem>
            {/* Oeffentliche Seite ausserhalb des Dashboard-Layouts, dieselbe
                wie in den Kunden-Mails. Neuer Tab statt <Link>, weil App.js
                oeffentliche Routen nur beim Laden der Seite erkennt. */}
            <a
              href='/product/updates'
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => trackEvent(trackEvents.NAVIGATE_TO_RELEASE_NOTES)}
              className={ITEM_CLASS_NAME}
            >
              <MegaphoneIcon className={ICON_CLASS_NAME} aria-hidden='true' />
              <span className='flex-1'>
                {t(TRANSLATION_LOCATION + 'navigation_release_notes')}
              </span>
              <ArrowUpRightIcon
                className={ICON_CLASS_NAME}
                aria-hidden='true'
              />
            </a>
          </MenuItem>
          {pricingOverride.available && (
            <>
              <Separator />
              <MenuItem>
                <button
                  type='button'
                  onClick={() => setIsPricingDialogOpen(true)}
                  className={ITEM_CLASS_NAME}
                >
                  <AdjustmentsHorizontalIcon
                    className={ICON_CLASS_NAME}
                    aria-hidden='true'
                  />
                  <span className='flex-1'>Pricing-Simulation</span>
                  {pricingOverride.active && (
                    <Badge text='aktiv' color='yellow' type='basic' />
                  )}
                </button>
              </MenuItem>
            </>
          )}
          <Separator />
          <MenuItem>
            <button type='button' onClick={signOut} className={ITEM_CLASS_NAME}>
              <ArrowRightStartOnRectangleIcon
                className={ICON_CLASS_NAME}
                aria-hidden='true'
              />
              {t('components.profile_dropdown.sign_out')}
            </button>
          </MenuItem>
          <Separator />
          {/* Rechtliche Links, von jeder Seite der App erreichbar */}
          <div className='flex gap-x-3 px-2 py-1.5'>
            {LEGAL_LINKS.map(({ key, href }) => (
              <MenuItem key={key}>
                <a
                  href={href}
                  target='_blank'
                  rel='noreferrer'
                  className='rounded text-xs text-gray-500 data-[focus]:text-gray-900 data-[focus]:underline'
                >
                  {t(`components.footer.${key}`)}
                </a>
              </MenuItem>
            ))}
          </div>
        </MenuItems>
      </Menu>
      {pricingOverride.available && (
        <AdminPricingDialog
          open={isPricingDialogOpen}
          onClose={() => setIsPricingDialogOpen(false)}
        />
      )}
    </>
  )
}

ProfileDropdown.propTypes = {
  isSidebarCollapsed: PropTypes.bool.isRequired
}

export default ProfileDropdown
